import { useCallback, useMemo, useState } from 'react';
import type { FacilitySpecialtyWrite } from '../../../../js/api/models/FacilitySpecialtyWrite';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';
import { EditSpecialtyDialog } from './EditSpecialtyDialog';

interface FacilitySpecialtiesListProps {
  facilityId: string;
  specialtyOptions: Array<{
    value: string;
    label: string;
  }>;
  specialties: any[];
  onUpdate?: (updatedSpecialties: any[]) => void;
  canEdit?: boolean;
}

export const FacilitySpecialtiesList = ({
  facilityId,
  specialtyOptions,
  specialties,
  onUpdate,
  canEdit = false,
}: FacilitySpecialtiesListProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [editingSpecialty, setEditingSpecialty] = useState<any | null>(null);
  const [deletingSpecialty, setDeletingSpecialty] = useState<any | null>(null);

  // Collect all choices_category values from dynamic fields across all specialties
  const dynamicChoicesCategories = useMemo(() => {
    const categories = new Set<string>();
    specialties.forEach((s) => {
      if (Array.isArray(s.dynamic_fields)) {
        s.dynamic_fields.forEach((field: any) => {
          if (
            (field.field_type === 'select' || field.field_type === 'multiselect') &&
            field.choices_category
          ) {
            categories.add(field.choices_category);
          }
        });
      }
    });
    return Array.from(categories);
  }, [specialties]);

  const { data: codeDefinitionsByCategory } =
    useCodeDefinitionsByCategories(dynamicChoicesCategories);

  const enrichDynamicFieldsWithChoices = useCallback(
    (fields: any[]): any[] => {
      if (!fields || !codeDefinitionsByCategory) return [];
      return fields.map((field) => {
        if (field.choices_category) {
          const categoryDefs = codeDefinitionsByCategory[field.choices_category] || [];
          const choices = categoryDefs.map((def) => ({ code: def.code, title: def.title }));
          return { ...field, choices };
        }
        return field;
      });
    },
    [codeDefinitionsByCategory]
  );

  const handleAdd = async (selectedSpecialties: string[]) => {
    if (selectedSpecialties.length === 0) return;

    setIsLoading(true);
    try {
      const newSpecialties = await Promise.all(
        selectedSpecialties.map((specialty) =>
          HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesCreate({
            requestBody: {
              facility: facilityId,
              specialty: specialty as any,
              dynamic_fields: '[]',
            } as FacilitySpecialtyWrite,
          })
        )
      );

      onUpdate?.(specialties.concat(newSpecialties));
    } catch (error) {
      console.error('Error adding specialties:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (specialtyId: string) => {
    try {
      await HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesDestroy({
        id: specialtyId,
      });
      onUpdate?.(specialties.filter((specialty) => specialty.id !== specialtyId));
    } catch (error) {
      console.error('Error removing specialty:', error);
    }
  };

  const handleSaveSpecialtyFields = async (specialtyId: string, dynamicFields: any) => {
    try {
      await HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesPartialUpdate({
        id: specialtyId,
        requestBody: {
          dynamic_fields: JSON.stringify(dynamicFields),
        },
      });
      onUpdate?.(
        specialties.map((s) => (s.id === specialtyId ? { ...s, dynamic_fields: dynamicFields } : s))
      );
    } catch (error) {
      console.error('Error updating specialty fields:', error);
      throw error;
    }
  };

  const specialtiesWithFields =
    specialties?.filter((s) => s.dynamic_fields && s.dynamic_fields.length > 0) || [];
  const specialtiesWithoutFields =
    specialties?.filter((s) => !s.dynamic_fields || s.dynamic_fields.length === 0) || [];

  return (
    <div className="tw:space-y-6">
      {specialties.length === 0 ? (
        <div className="tw:text-zinc-500 tw:dark:text-zinc-400">No specialties yet.</div>
      ) : (
        <>
          {[...specialtiesWithFields]
            .sort((a, b) => {
              const aHasFields = a.dynamic_fields && a.dynamic_fields.length > 0;
              const bHasFields = b.dynamic_fields && b.dynamic_fields.length > 0;
              return bHasFields ? 1 : aHasFields ? -1 : 0;
            })
            .map((specialty) => {
              const specialtyLabel =
                specialtyOptions.find((opt) => opt.value === specialty.specialty)?.label ||
                specialty.specialty;
              const dynamicFields = specialty.dynamic_fields;
              const hasDynamicFields = dynamicFields && dynamicFields.length > 0;

              return (
                <div
                  key={specialty.id}
                  className="tw:relative tw:bg-white tw:rounded-xl tw:border tw:border-zinc-200 tw:p-6 tw:mb-6 tw:shadow-sm tw:dark:bg-zinc-700 tw:dark:border-white/10"
                >
                  <div className="tw:flex tw:items-center tw:mb-4">
                    <h6 className="tw:text-lg tw:font-semibold tw:text-zinc-900 tw:mr-2 tw:!mb-0 tw:dark:!text-zinc-400">
                      {specialtyLabel}
                    </h6>
                    {canEdit && (
                      <div className="tw:ml-auto tw:flex tw:gap-2">
                        {hasDynamicFields && (
                          <Button
                            type="button"
                            plain={true}
                            onClick={() =>
                              setEditingSpecialty({
                                ...specialty,
                                title: specialtyLabel,
                              })
                            }
                            className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:p-2 tw:dark:text-zinc-300 tw:dark:hover:text-brand-green-1"
                            aria-label={`Edit ${specialtyLabel}`}
                          >
                            <i className="fa-regular fa-pen-to-square" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          plain={true}
                          onClick={() => setDeletingSpecialty(specialty)}
                          className="tw:p-2 tw:text-zinc-400 tw:hover:text-stoplight-danger tw:dark:text-zinc-300 tw:dark:hover:!text-stoplight-danger"
                          aria-label={`Delete ${specialtyLabel}`}
                        >
                          <i className="fa-regular fa-trash" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="tw:bg-zinc-50 tw:rounded-lg tw:p-4 tw:dark:bg-white/5">
                    <DynamicFieldFactory
                      fields={enrichDynamicFieldsWithChoices(dynamicFields)}
                      onChange={() => {}}
                      readOnly={true}
                      columns={2}
                    />
                  </div>
                </div>
              );
            })}

          {specialtiesWithoutFields.length > 0 && specialtiesWithFields.length === 0 && (
            <div className="tw:divide-y tw:divide-zinc-100 tw:dark:!divide-white/10">
              {specialtiesWithoutFields.map((specialty) => {
                const specialtyLabel =
                  specialtyOptions.find((opt) => opt.value === specialty.specialty)?.label ||
                  specialty.specialty;
                return (
                  <div
                    key={specialty.id}
                    className="tw:flex tw:items-center tw:justify-between tw:px-0 tw:py-4"
                  >
                    <span className="tw:text-zinc-900 tw:dark:text-zinc-100 tw:font-medium">
                      {specialtyLabel}
                    </span>
                    {canEdit && (
                      <Button
                        type="button"
                        plain={true}
                        onClick={() => setDeletingSpecialty(specialty)}
                        className="tw:p-2 tw:text-zinc-400 tw:hover:text-stoplight-danger tw:dark:text-zinc-300 tw:dark:hover:!text-stoplight-danger"
                        aria-label={`Delete ${specialtyLabel}`}
                      >
                        <i className="fa-regular fa-trash" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {specialtiesWithoutFields.length > 0 && specialtiesWithFields.length > 0 && (
            <div className="tw:mt-10 tw:rounded-xl tw:border tw:border-zinc-200 tw:p-6 tw:mb-6 tw:shadow-sm tw:dark:bg-zinc-700 tw:dark:border-white/10">
              <h6 className="tw:text-base tw:font-semibold tw:text-zinc-900 tw:mb-2 tw:pb-4 tw:border-b tw:border-zinc-200 tw:dark:!text-zinc-100 tw:dark:border-zinc-700">
                Other Specialties
              </h6>
              <div className="tw:divide-y tw:divide-zinc-100 tw:dark:!divide-white/10">
                {specialtiesWithoutFields.map((specialty) => {
                  const specialtyLabel =
                    specialtyOptions.find((opt) => opt.value === specialty.specialty)?.label ||
                    specialty.specialty;
                  return (
                    <div
                      key={specialty.id}
                      className="tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4"
                    >
                      <span className="tw:text-zinc-900 tw:dark:text-zinc-100 tw:font-medium">
                        {specialtyLabel}
                      </span>
                      {canEdit && (
                        <Button
                          type="button"
                          plain={true}
                          onClick={() => setDeletingSpecialty(specialty)}
                          className="tw:p-2 tw:text-zinc-400 tw:hover:text-stoplight-danger tw:dark:text-zinc-300 tw:dark:hover:!text-stoplight-danger"
                          aria-label={`Delete ${specialtyLabel}`}
                        >
                          <i className="fa-regular fa-trash" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {editingSpecialty && (
            <EditSpecialtyDialog
              open={!!editingSpecialty}
              onClose={() => setEditingSpecialty(null)}
              specialty={editingSpecialty}
              onSave={handleSaveSpecialtyFields}
              onSuccess={() => {
                setEditingSpecialty(null);
                onUpdate?.(specialties);
              }}
            />
          )}

          {deletingSpecialty && (
            <Dialog
              open={!!deletingSpecialty}
              onClose={() => setDeletingSpecialty(null)}
              className="tw:p-6"
            >
              <DialogTitle className="tw:dark:!text-white/90">Remove Specialty</DialogTitle>
              <DialogBody>
                <p className="tw:text-zinc-900 tw:dark:text-zinc-100">
                  Are you sure you want to remove this specialty?
                </p>
                <div className="tw:font-semibold tw:text-zinc-900 tw:dark:text-zinc-100">
                  {specialtyOptions.find((opt) => opt.value === deletingSpecialty.specialty)
                    ?.label || deletingSpecialty.specialty}
                </div>
                <p className="tw:mt-2 tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
                  This will remove all field values associated with this specialty (if any).
                </p>
              </DialogBody>
              <DialogActions>
                <Button type="button" plain onClick={() => setDeletingSpecialty(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  plain
                  className="tw:bg-stoplight-danger tw:!text-white tw:hover:!bg-stoplight-danger/90"
                  onClick={() => {
                    if (deletingSpecialty) {
                      handleRemove(deletingSpecialty.id);
                      setDeletingSpecialty(null);
                    }
                  }}
                >
                  Remove
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}
    </div>
  );
};
